import {
    CalendarIcon,
    EllipseFilledIcon,
    EllipseHalfIcon,
    LadderIcon,
    LocationIcon,
    TrashIcon,
    WorldIcon,
} from '@bi-digital/icons';
import {
    Alert,
    Button,
    Card,
    CardAction,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    useMediaQuery,
} from '@bi-digital/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { BiaxCartItem } from '../../../types';
import styles from './cartCardItem.module.css';

export interface CartCardItemProps {
    item: BiaxCartItem;
    itemBeingDeleted: string | null;
    removeFromCart: (item: BiaxCartItem) => void;
    deleteError: string | null;
}

export default function CartCardItem({ item, itemBeingDeleted, deleteError, removeFromCart }: CartCardItemProps) {
    const { bannerId, campus, label, credits, name, url, language, term, load, studentType } = item;
    const { t } = useTranslation(['cart', 'common']);
    const isSmallScreen = useMediaQuery('(width < 768px)');
    const titleLinkId = useId();
    return (
        <div className={styles.cardContainer}>
            <Card
                data-color='neutral'
                orientation='horizontal'
                error={deleteError === bannerId}
                data-clickdelegatefor={url ? titleLinkId : undefined}
            >
                <CardContent>
                    <CardHeader
                        label={label}
                        subLabel={t('cart:credits', { count: credits })}
                        header={
                            url ? (
                                <a id={titleLinkId} href={url}>
                                    {name}
                                </a>
                            ) : (
                                name
                            )
                        }
                    />

                    <div className={styles.chipContainer}>
                        <Chip
                            size={isSmallScreen ? 'small' : 'medium'}
                            startIcon={
                                load === 'FullTime' ? (
                                    <EllipseFilledIcon size='small' aria-hidden='true' />
                                ) : (
                                    <EllipseHalfIcon size='small' aria-hidden='true' />
                                )
                            }
                        >
                            {t(`cart:load.${load}`)}
                        </Chip>
                        <Chip
                            size={isSmallScreen ? 'small' : 'medium'}
                            startIcon={<CalendarIcon size='small' aria-hidden='true' />}
                        >
                            {t(`cart:${term.semester.toLowerCase()}`, { year: term.year })}
                        </Chip>
                        <Chip
                            size={isSmallScreen ? 'small' : 'medium'}
                            startIcon={<WorldIcon size='small' aria-hidden='true' />}
                        >
                            {t(`cart:language.${language}`)}
                        </Chip>
                        <Chip
                            size={isSmallScreen ? 'small' : 'medium'}
                            data-color={campus === 'Online' ? 'info' : undefined}
                            startIcon={<LocationIcon size='small' aria-hidden='true' />}
                        >
                            {t(`cart:campus.${campus}`)}
                        </Chip>
                        {(studentType == 'SecondYear' || studentType == 'ThirdYear') && (
                            <Chip
                                size={isSmallScreen ? 'small' : 'medium'}
                                data-color='warning'
                                startIcon={<LadderIcon size='small' aria-hidden='true' />}
                            >
                                {t('cart:studentypeText') + ' ' + t(`cart:studentype.${studentType}`)}
                            </Chip>
                        )}
                    </div>
                </CardContent>

                <CardActions>
                    <CardAction onClick={() => removeFromCart(item)} disabled={itemBeingDeleted === bannerId} asChild>
                        <Button data-color='primary' icon variant='text' submitting={itemBeingDeleted === bannerId}>
                            <TrashIcon size='medium' title={t('common:delete')}></TrashIcon>
                        </Button>
                    </CardAction>
                </CardActions>
            </Card>
            {deleteError === bannerId && (
                <Alert className={styles.error} data-color='alert'>
                    {t('cart:errorDeletingCartItem')}
                </Alert>
            )}
        </div>
    );
}
